import { html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import type { ImageGalleryItem, ImageGalleryItemGridPosition, ImageGalleryItemObjectFit } from '../image-gallery-item/image-gallery-item.model';
import styles from './image-gallery-item.styles';

/**
 * @slot - The component content
 * @csspart base - The component's base wrapper.
 * @csspart image - The component's image for image item
 * @csspart iframe - The component's iframe for video item.
 *  @csspart part-default - The component's default slot.
 */

export class BlocksImageGalleryItem extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Indicates if the panel is currently active.
   */
  @property({ type: Boolean, reflect: true })
  isActive = false;

  /**
   * state to maintain selected image
   */
  @property({ type: Object }) data: ImageGalleryItem | undefined;

  @property({ type: String })
  objectFit: ImageGalleryItemObjectFit;

  /**
   * Grid position prop
   */
  @property({ type: String })
  gridPosition: ImageGalleryItemGridPosition = 'left';

  protected slots = new SlotController(this, null);

  private renderThumbnails() {
    return html`
      ${this.data?.type !== 'video'
        ? html`
            <img part="image" class="cat-c-image-gallery__img" src=${ifDefined(this.data?.src)} alt=${ifDefined(this.data?.alt)} loading="lazy" />
          `
        : html`
            <iframe part="iframe" class="cat-c-image-gallery__iframe" src=${this.data?.src + '?controls=1&embedded=true'} loading="lazy"> </iframe>
          `}
    `;
  }

  render() {
    const componentClassNames = this.componentClassNames('cat-c-image-gallery-item ', {
      'cat-c-image-gallery-item--selected': this.isActive,
      'cat-c-image-gallery--right': this.gridPosition === 'right',
      'cat-c-image-gallery--bottom': this.gridPosition === 'bottom',
      'cat-c-image-gallery--cover': this.objectFit === 'cover',
      'cat-c-image-gallery--fill': this.objectFit === 'fill',
      'cat-c-image-gallery--none': this.objectFit === 'none'
    });

    return html`
      <div part="base" class="${componentClassNames}">
        ${!this.slots.isEmpty() ? (this.isActive ? html`<slot part="part-default"></slot>` : nothing) : this.renderThumbnails()}
      </div>
    `;
  }
}

if (customElements.get('cat-image-gallery-item') === undefined) {
  customElements.define('cat-image-gallery-item', BlocksImageGalleryItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-image-gallery-item': BlocksImageGalleryItem;
  }
}
